import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { RootReducerState } from 'types/global'
import { IIshikawaCategory, NotificationAction, NotificationReducer } from 'types/notification'
import NotificationActions from 'actions/Notification'
import { Dispatch } from 'redux'
import { Beforeunload } from 'react-beforeunload'
import {
    IshikawaContainer,
    IshikawaMain,
    SaveButton,
    TitleIshikawaForm,
    Top,
    TopInputs,
    BackButton,
    Subtitle,
    inputRequiredStyle
} from 'pages/NotificationTreatment/NotificationsTreatmentsSteps/IshikawaDiagram/styles'
import { Breadcrumbs, SecondTextBreadcrumbs } from 'pages/NotificationTreatment/NotificationsTreatmentsSteps/Header/styles'
import { InputLabel } from 'components/InputLabel'
import { Label } from 'components/InputLabel/styles'
import { FieldErrors, FieldValues, UseFormGetValues, UseFormRegister, UseFormSetValue, useForm } from 'react-hook-form'
import GroupDetails from '../GroupActionPlan/Children/GroupDetails'
import { Confirm } from 'components/Modals/Confirm'
import { SubTitleText, TitleText } from 'pages/NotifyEvent/styles'
import { IconWarning } from 'components/NavBar/svg'
import NotificationService from 'services/Notification'
import NumberOrBulletTextArea from 'components/NumberOrBulletTextArea'
import { useCallbackPrompt } from 'hooks/useCallbackPrompt'
import { IAuthenticationReducer } from 'types/autentication'
import { convertIshikawaFullFormUpdateBatchRequest, convertIshikawaToBatchRequestForNotifications, GroupData, buildIshikawaSysIdByNotificationMap, mergeFirstIshikawaIdFromRedux, partitionNotificationsByProtocolMap } from './helpers'
import { ROUTES_NAMES } from 'constants/name.routes'

interface FieldType {
    name: string;
    label: string;
    subLabel: string;
}

interface FieldTypeProp extends FieldType {
    setValue: UseFormSetValue<FieldValues>
    register: UseFormRegister<FieldValues>
    getValues: UseFormGetValues<FieldValues>
    errors: FieldErrors<FieldValues>
    onChangeHelper: (event: React.ChangeEvent<HTMLTextAreaElement>) => void
}

const GroupIshikawaForm: React.FC = () => {
    const { id } = useParams()
    const navigate = useNavigate()

    const notificationDispatch: Dispatch<NotificationAction> = useDispatch<Dispatch<NotificationAction>>()

    const [isDirty, setIsDirty] = useState<boolean>(false)
    const [isSaving, setIsSaving] = useState(false)
    const [groupData, setGroupData] = useState<GroupData | null>(null)
    const [loadingGroupData, setLoadingGroupData] = useState(true)

    const MSG_SUCCESS = `Análise de Ishikawa em grupo realizada com sucesso!`

    const STATE_FIELDS = {
        method: { title: 'MÉTODO' },
        measurement: { title: 'MEDIDA' },
        material: { title: 'MATERIAL' },
        environment: { title: 'MEIO AMBIENTE' },
        machine: { title: 'MÁQUINA' },
        manpower: { title: 'MÃO DE OBRA' }
    }

    const fields: FieldType[] = [
        {
            name: 'MÉTODO',
            label: 'MÉTODO',
            subLabel: 'Qualquer fator relacionado com procedimento.',
        },
        {
            name: 'MEDIDA',
            label: 'MEDIDA',
            subLabel: 'Qualquer fator relacionado com o monitoramento ou as métricas de controle do processo.',
        },
        {
            name: 'MÃO DE OBRA',
            label: 'MÃO DE OBRA',
            subLabel: 'Qualquer fator relacionado a falha humana ou pessoas.',
        },
        {
            name: 'MEIO AMBIENTE',
            label: 'MEIO AMBIENTE',
            subLabel: 'Qualquer fator relacionado com infra-estrutura e ambientes externos e internos.',
        },
        {
            name: 'MATERIAL',
            label: 'MATERIAL',
            subLabel: 'Qualquer fator relacionado a itens, componentes, insumos ou matérias-primas.',
        },
        {
            name: 'MÁQUINA',
            label: 'MÁQUINA',
            subLabel: 'Qualquer fator relacionado a equipamentos.',
        },
    ]

    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        getValues
    } = useForm()

    const auth: IAuthenticationReducer = useSelector(
        (state: RootReducerState) => state.authenticationReducers
    )

    const notificationSelector: NotificationReducer = useSelector(
        (state: RootReducerState) => state.notificationsReducer
    )

    // Buscar dados do grupo
    useEffect(() => {
        const fetchGroupData = async () => {
            if (!id) return

            try {
                setLoadingGroupData(true)
                const groupResponse = await NotificationService.getGroupDataById(id)
                setGroupData(groupResponse)
            } catch (error) {
                console.error('Erro ao buscar dados do grupo:', error)
            } finally {
                setLoadingGroupData(false)
            }
        }

        fetchGroupData()
    }, [id])

    // Buscar diagrama existente da PRIMEIRA notificação do grupo
    useEffect(() => {
        if (groupData?.notifications?.length > 0) {
            const firstNotificationId = groupData.notifications[0]?.sys_id?.value
            if (firstNotificationId) {
                NotificationActions.getIshikawaByNotificationId(notificationDispatch, firstNotificationId)
            }
        }

        if (!notificationSelector.notificationById) {
            NotificationActions.getNotificationById(notificationDispatch, id)
        }
    }, [groupData])

    // Preencher formulário com dados existentes
    useEffect(() => {
        const ishikawa = notificationSelector.ishikawaDiagram;
        if (ishikawa && ishikawa.categories) {
            let categories = [...ishikawa.categories]
            redrawAllCauses(categories)
        }
    }, [notificationSelector.ishikawaDiagram])

    function redrawAllCauses(categories: IIshikawaCategory[]) {
        for (let cat of categories) {
            for (let field in STATE_FIELDS) {
                let lis = []
                if (STATE_FIELDS.hasOwnProperty(field) && encodeURIComponent(cat.title.trim()) === encodeURIComponent(STATE_FIELDS[field].title)) {
                    if (cat.causes.length > 0) {
                        for (let cause of cat.causes) {
                            if (cause.length > 0) {
                                const text = `• ${cause.trim()}\n`
                                lis.push(text)
                            }
                        }
                        setValue(STATE_FIELDS[field].title, lis.join(''))
                    }
                }
            }
        }
        return categories
    }

    const handleSubmitForm = async () => {
        setIsSaving(true)

        if (!groupData || !auth.dataJwt) {
            console.error('Dados do grupo ou usuário não encontrados')
            setIsSaving(false)
            return
        }

        const ishikawaData = {
            material: buildStringFieldRequest(getValues(STATE_FIELDS.material.title)),
            environment: buildStringFieldRequest(getValues(STATE_FIELDS.environment.title)),
            machine: buildStringFieldRequest(getValues(STATE_FIELDS.machine.title)),
            manpower: buildStringFieldRequest(getValues(STATE_FIELDS.manpower.title)),
            measurement: buildStringFieldRequest(getValues(STATE_FIELDS.measurement.title)),
            method: buildStringFieldRequest(getValues(STATE_FIELDS.method.title)),
            description: getValues('description') || groupData.notifications[0]?.u_reclassified_incident_type?.display_value || notificationSelector.notificationById?.u_reclassified_incident_type?.display_value || ''
        }

        try {
            const ishikawaIdByNotif = mergeFirstIshikawaIdFromRedux(
                groupData,
                await buildIshikawaSysIdByNotificationMap(groupData),
                notificationSelector.ishikawaDiagram
            )
            const { withProtocol: withIshikawa, withoutProtocol: withoutIshikawa } =
                partitionNotificationsByProtocolMap(groupData as any, ishikawaIdByNotif)

            if (withIshikawa.length > 0) {
                await NotificationActions.updateIshikawaBatch(
                    notificationDispatch,
                    convertIshikawaFullFormUpdateBatchRequest(
                        withIshikawa as any,
                        ishikawaData,
                        ishikawaIdByNotif,
                        auth.dataJwt,
                        groupData
                    )
                )
            }
            if (withoutIshikawa.length > 0) {
                await NotificationActions.createIshikawaBatch(
                    notificationDispatch,
                    convertIshikawaToBatchRequestForNotifications(
                        withoutIshikawa as any,
                        ishikawaData,
                        groupData,
                        auth.dataJwt
                    )
                )
            }

            if (groupData?.notifications?.length > 0) {
                const firstNotificationId = groupData.notifications[0]?.sys_id?.value
                if (firstNotificationId) {
                    await NotificationActions.getIshikawaByNotificationId(
                        notificationDispatch,
                        firstNotificationId
                    )
                }
            }

            // Navegar de volta com mensagem de sucesso
            navigate(`/notificacoes/group-treatment/${id}`, {
                state: {
                    currentStep: 2,
                    messageSuccess: MSG_SUCCESS
                }
            })
        } catch (error) {
            console.error('Erro ao salvar diagrama de Ishikawa em grupo:', error)
        } finally {
            setIsSaving(false)
        }
    }

    const [showPrompt, confirmNavigation, cancelNavigation] =
        useCallbackPrompt(isDirty && !isSaving)

    const buildStringFieldRequest = (categories: string) => {
        const regex = /• \s*/g;
        categories = categories.replace(/• \s*/, '');
        const clean = categories.replace(regex, ' |')
        return clean
    }

    const clearAndRedirectIshikawaDiagram = () => {
        navigate(`/notificacoes/group-treatment/${id}`, { state: { currentStep: 2, messageSuccess: null } })
    }

    const backIshikawaDiagram = useCallback((e: any) => {
        if (!isDirty || hasWriting()) {
            clearAndRedirectIshikawaDiagram()
        }
    }, [isDirty])

    const onChangeHelper = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setIsDirty(hasWriting())
    }

    const hasWriting = () => {
        const regex = /• \s*/g;
        let names = ['MÉTODO', 'MEDIDA', 'MÃO DE OBRA', 'MEIO AMBIENTE', 'MATERIAL', 'MÁQUINA']

        let existContent = names.map(item => {
            let fieldName = getValues(item);
            let len = fieldName?.length;
            if (len > 0) {
                let content = fieldName?.replace(regex, '').trim();
                return content.length > 0
            }
            return false;
        })

        return existContent.filter(i => i).length > 0
    }

    const handleViewNotification = useCallback((notificationId: string) => {
        if (!notificationId || !groupData) return

        // Coletar todos os sys_ids das notificações do grupo
        const notificationIds = groupData.notifications
            ?.map(notification => notification.sys_id?.value)
            .filter(id => id) || []

        navigate(`/notificacoes/view-group`, {
            state: {
                fromGroup: true,
                groupId: id,
                notificationIds: notificationIds,
                initialNotificationId: notificationId,
                fromIshikawaEdit: true  // Indicar que está vindo da edição do diagrama de Ishikawa
            }
        })
    }, [groupData, id, navigate])

    return (
        <>
            {isDirty && (
                <Beforeunload onBeforeunload={(event) => 'Você fez edições que não foram salvas.'} />
            )}
            <Confirm
                isOpen={showPrompt as boolean}
                handleConfirmModal={confirmNavigation as any}
                handleCloseModal={cancelNavigation as any}
                labelButtomClose={`Sair`}
                labelButtomConfirm={`Continuar edição`}
            >
                <div className='justify-center flex p-1'>
                    <IconWarning></IconWarning>
                </div>
                <TitleText className={'text-yellow text-xl font-semibold justify-center flex'}>
                    Edição não salva
                </TitleText>
                <div className='justify-center flex'>
                    <SubTitleText>
                        <p>Você fez edições que não foram salvas.</p>
                        <p>Se sair, você perderá as edições.</p>
                    </SubTitleText>
                </div>
            </Confirm>

            <IshikawaContainer data-testid="test-group-ishikawa-form">
                <IshikawaMain onSubmit={handleSubmit(handleSubmitForm)}>
                    {/* <div className='flex items-baseline mt-8 justify-between'>
                        <Breadcrumbs>
                            <Link className="mr-1 cursor-pointer" to="/notificacoes">
                                Gestão das notificações
                            </Link>
                            {' > '}
                            <span className="mr-1 cursor-pointer" onClick={backIshikawaDiagram}>
                                Tratamento em Grupo
                            </span>
                            {' > '}
                            <SecondTextBreadcrumbs>Análise de Ishikawa</SecondTextBreadcrumbs>
                        </Breadcrumbs>
                        
                    </div> */}

                    <div className='overflow-y-auto'>
                        <Top className='mb-0'>
                            <TitleIshikawaForm data-testid='group-ishikawa-notifications'>
                                Fazer a Análise do Diagrama de Ishikawa
                            </TitleIshikawaForm>

                            <TopInputs>
                                <BackButton type='button' onClick={backIshikawaDiagram}>Voltar</BackButton>
                                <SaveButton type='submit' className={`${!isDirty ? 'bg-[#E2E2E2]' : ''}`} disabled={!isDirty || isSaving}>
                                    Salvar
                                </SaveButton>
                            </TopInputs>
                            
                            <Subtitle>
                                Identifique os principais fatores que levaram o
                                incidente acontecer, e preeencha as causas relacionadas
                                as classes do diagrama:
                            </Subtitle>
                        </Top>

                        <div className="grid grid-cols-6 mt-6 gap-10">
                            <div className='col-span-6 xl:col-span-4'>
                                <div className='container max-w-full'>
                                    <div className='w-full px-8 mt-6 mb-10'>
                                        <div>
                                            <span>
                                                <Label className='p-0 text-xs font-semibold text-darkGray'>
                                                    EFEITO INESPERADO
                                                </Label>
                                            </span>

                                            <InputLabel
                                                defaultValue={
                                                    groupData?.notifications?.[0]?.u_reclassified_incident_type?.display_value ||
                                                    notificationSelector.notificationById?.u_reclassified_incident_type?.display_value ||
                                                    ''
                                                }
                                                name='description'
                                                register={register}
                                                className={`max-w-full`}
                                                placeholder='Digite aqui'
                                                inputLength={80}
                                                maxLength={80}
                                                readOnly={true}
                                                error={{
                                                    message: ''
                                                }}
                                            />
                                        </div>
                                        <div
                                            data-testid="test-group-text-area-container"
                                            className='flex flex-wrap gap-4'>
                                            {
                                                fields.map((field, idx) => {
                                                    return (
                                                        <div key={idx}>
                                                            <TextEditableList
                                                                name={field.name}
                                                                label={field.label}
                                                                subLabel={field.subLabel}
                                                                setValue={setValue}
                                                                register={register}
                                                                getValues={getValues}
                                                                errors={errors}
                                                                onChangeHelper={onChangeHelper}
                                                            />
                                                        </div>
                                                    )
                                                })
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className='col-span-6 xl:col-span-2 px-8'>
                                <GroupDetails
                                    groupData={groupData}
                                    onViewNotification={handleViewNotification}
                                />
                            </div>
                        </div>
                    </div>
                </IshikawaMain>
            </IshikawaContainer>
        </>
    )
}

const TextEditableList = ({ label, subLabel, name, register, setValue, getValues, errors, onChangeHelper }: FieldTypeProp) => {
    return (
        <>
            <div className='flex flex-col gap-4 sm:w-[365px] xl:min-w-[365px] 2xl:min-w-[400px] mt-[34px]'>
                <span className='h-[35px]'>
                    <Label className='p-0 text-xs font-semibold text-darkGray'>{label}</Label>
                    <Label className='text-xs text-darkGray/80 font-normal whitespace-normal'>
                        {subLabel}
                    </Label>
                </span>

                <div className={`flex items-center relative`}>
                    <NumberOrBulletTextArea
                        maxLength={1000}
                        isDotOrNumber="dot"
                        setValue={setValue}
                        getValues={getValues}
                        name={name}
                        register={register}
                        textAreaStyle={`sm:w-[365px] xl:min-w-[365px] xxl:min 2xl:min-w-[400px] h-[260px] focus:border-1 focus:border-black ${errors[name] && inputRequiredStyle}`}
                        placeholder="&#8226; Digite aqui"
                        rows={8}
                        onChangeHelper={onChangeHelper}
                    />
                </div>
            </div>
        </>
    )
}

export { GroupIshikawaForm }